import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectLatestWorkForDocument } from '../../../../store/slices/workSlice'
import { selectDocument as selectAnalysisDocument, initializePendingAnalysis } from '../../../../store/slices/analysisSlice'
import { setAnnotations as setGlobalAnnotations } from '../../../../store/slices/documentsSlice'
import { AlertCircle, Loader2 } from 'lucide-react'
import { FeatureGuard } from '../../../../hooks/useFeatures'
import WorkPanelWrapper from '../WorkPanelWrapper'
import type { ViewerProvider } from '../../../ai-viewer/provider/types'
import type { ViewerEventBus } from '../../../ai-viewer/eventing'
import type { ExtractedField, BulkAction } from '../../../ai-viewer/eventing/extraction-types'
import type { Annotation } from '../../../ai-viewer/eventing/types'

// Custom hooks
import { useDocumentLoader } from './hooks/useDocumentLoader'
import { useUrlStateSync, useSyncPageToUrl } from './hooks/useUrlStateSync'
import { useAnnotationManager } from './hooks/useAnnotationManager'
import { useAnalysisBuilder } from './hooks/useAnalysisBuilder'
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts'

// Sub-components
import { DocumentActions } from './components/DocumentActions'
import { PageNavigation } from './components/PageNavigation'
import { ProcessingModal } from './components/ProcessingModal'
import { FieldsSummaryPanel } from './components/FieldsSummaryPanel'

// Dynamic imports for client-only components
const SimplePDFHighlighter = React.lazy(() => import('./PDFHighlighter'))
const DraggableAnnotationEditor = React.lazy(() => import('../DraggableAnnotationEditor'))
const ExtractionPanel = React.lazy(() => import('../../../ai-viewer/overlay/ExtractionPanel'))

interface FileViewerProps {
  documentId?: string
  localFileUrl?: string
  hideHeader?: boolean
  isModal?: boolean
}


export const FileViewer: React.FC<FileViewerProps> = ({ documentId, localFileUrl, hideHeader = false, isModal = false }) => {
  const dispatch = useAppDispatch()
  const { workId: workIdFromUrl } = useParams<{ workId: string }>()
  
  // Get work item for this document (only if documentId is provided)
  const workItem = useAppSelector(state => documentId ? selectLatestWorkForDocument(state, documentId) : null)
  
  // Read directly from Redux (single source of truth)
  const annotations = useAppSelector(state => state.documents.annotations)
  const fields = useAppSelector(state => state.documents.fields)
  
  // Local state (UI only)
  const [currentPage, setCurrentPage] = useState(1)
  const [numPages, setNumPages] = useState(0)
  const [provider, setProvider] = useState<ViewerProvider | null>(null)
  const [, setBus] = useState<ViewerEventBus | null>(null)
  const [highlightedAnnotations, setHighlightedAnnotations] = useState<Set<string>>(new Set())
  
  // Derive file info from work item
  const fileName = workItem?.subject?.fileName || 'Document'
  const isProcessing = false; //workItem?.status === 'running' || workItem?.status === 'pending'
  const progress = workItem?.runtime?.progress || 0
  const progressMessage = workItem?.runtime?.message || 'Processing...'

  // URL state - read once on mount
  const [searchParams] = useSearchParams()
  const hasInitializedFromUrl = useRef(false)
  const hasLoadedAnnotationFromUrl = useRef(false)
  
  // Custom hooks
  const { updateUrlParams } = useUrlStateSync()
  const { documentUrl, semanticExtraction, loading, error } = useDocumentLoader({
    documentId,
    workItem,
    localFileUrl
  })
  
  // Create a no-op URL update function for modal mode
  const urlUpdateFn = isModal ? () => {} : updateUrlParams
  
  const {
    selectedAnnotationId,
    currentAnnotationIndex,
    handleAnnotationUpdate,
    handleAnnotationCreate,
    handleNextAnnotation,
    handlePrevAnnotation,
    setSelectedAnnotationId
  } = useAnnotationManager({
    currentPage,
    onPageChange: setCurrentPage,
    onUrlUpdate: urlUpdateFn
  })

  const { isDrawing, setIsDrawing } = useKeyboardShortcuts({
    onNextAnnotation: handleNextAnnotation,
    onPrevAnnotation: handlePrevAnnotation
  })

  // Build and dispatch analysis data (only if documentId is provided)
  useAnalysisBuilder({
    semanticExtraction,
    workItem,
    documentId
  })

  // Sync page changes to URL (only after initialization, and only if not in modal mode)
  useSyncPageToUrl(currentPage, isModal ? () => {} : updateUrlParams, hasInitializedFromUrl, workIdFromUrl)

  // Clear state when documentId changes (switching to a new document)
  useEffect(() => {
    setHighlightedAnnotations(new Set())
    setSelectedAnnotationId(null)
    setCurrentPage(1)
    
    // Clear Redux state for this document
    dispatch(setGlobalAnnotations([]))
    
    // Select this document for analysis display (only if documentId is provided)
    if (documentId) {
      dispatch(selectAnalysisDocument(documentId))
    }
    
    // Initialize pending analysis if workItem exists
    if (workItem?.id && documentId) {
      dispatch(initializePendingAnalysis({
        documentId,
        workId: workItem.id
      }))
    }

    // Clear URL params when switching documents (only if not in modal mode)
    if (!isModal) {
      updateUrlParams({
        page: null,
        annotation: null,
        highlight: null,
        entity: null
      })
    }
    
    // Reset initialization flags for new document
    hasInitializedFromUrl.current = false
    hasLoadedAnnotationFromUrl.current = false
  }, [documentId, workItem?.id, dispatch, updateUrlParams, isModal])

  // Initialize state from URL params - ONLY ONCE on initial load (skip in modal mode)
  useEffect(() => {
    if (hasInitializedFromUrl.current || isModal) return
    
    const urlPage = searchParams.get('page')
    const urlHighlight = searchParams.get('highlight')
    
    // Read page from URL
    if (urlPage) {
      const page = parseInt(urlPage, 10)
      if (!isNaN(page) && page > 0) {
        setCurrentPage(page)
      }
    }
    
    // Read highlights from URL
    if (urlHighlight) {
      const ids = urlHighlight.split(',')
      setHighlightedAnnotations(new Set(ids))
    }
    
    // Mark as initialized - State→URL sync can start now
    hasInitializedFromUrl.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModal]) // Run only once on mount, or when isModal changes
  
  // Handle annotation from URL separately (needs to wait for annotations to load)
  // This runs once when annotations are available (skip in modal mode)
  useEffect(() => {
    if (hasLoadedAnnotationFromUrl.current || isModal) return
    if (annotations.length === 0) return
    
    const urlAnnotationId = searchParams.get('annotation')
    if (urlAnnotationId) {
      const annotation = annotations.find(a => a.id === urlAnnotationId)
      if (annotation) {
        setSelectedAnnotationId(urlAnnotationId)
        if (annotation.page) {
          setCurrentPage(annotation.page)
        }
      }
    }
    
    hasLoadedAnnotationFromUrl.current = true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotations.length > 0, isModal]) // Run when annotations load

  // Memoize selected annotation lookup
  const selectedAnnotation = useMemo(
    () => annotations.find(a => a.id === selectedAnnotationId),
    [annotations, selectedAnnotationId]
  )

  // Handlers
  const handlePdfReady = useCallback((pdfProvider: ViewerProvider, eventBus: ViewerEventBus) => {
    setProvider(pdfProvider)
    setBus(eventBus)
    
    // Subscribe to page changes
    eventBus.on('PageChanged', (event) => {
      setCurrentPage(event.page)
    })
  }, [])

  const handleFieldClick = useCallback((field: ExtractedField) => {
    if (field.annotations.length > 0) {
      const firstAnnotation = field.annotations[0]
      const annotationIds = field.annotations.map(a => a.id)
      
      setSelectedAnnotationId(firstAnnotation.id)
      setHighlightedAnnotations(new Set(annotationIds))
      
      if (firstAnnotation.page) {
        setCurrentPage(firstAnnotation.page)
      }
      
      if (!isModal) {
        updateUrlParams({
          page: firstAnnotation.page?.toString() || null,
          annotation: firstAnnotation.id,
          highlight: annotationIds.join(','),
          entity: field.id
        })
      }
    }
  }, [updateUrlParams, isModal])

  const handleEntityClick = useCallback((entityAnnotations: Annotation[]) => {
    const annotationIds = entityAnnotations.map(a => a.id)
    
    setHighlightedAnnotations(new Set(annotationIds))
    
    if (entityAnnotations.length > 0 && entityAnnotations[0].page) {
      setCurrentPage(entityAnnotations[0].page)
      
      if (!isModal) {
        updateUrlParams({
          page: entityAnnotations[0].page.toString(),
          highlight: annotationIds.join(','),
          annotation: null
        })
      }
    }
  }, [updateUrlParams, isModal])

  const handleFieldUpdate = useCallback((_field: ExtractedField, _newValue: string) => {
    // TODO: Update the field value in Firestore
    console.log('Field update not yet implemented')
  }, [])

  const handleBulkAction = useCallback((action: BulkAction) => {
    console.log('Bulk action:', action)
    // TODO: Implement bulk actions
  }, [])


  const handleToggleDrawing = useCallback(() => {
    setIsDrawing(prev => !prev)
  }, [setIsDrawing])

  // Render loading state
  const loadingContent = (
    <div className="flex items-center justify-center h-full min-h-[400px]">
      <div className="flex flex-col items-center gap-6">
        <Loader2 className="w-16 h-16 animate-spin text-orange-500" />
        <p className="text-base font-medium text-gray-700">Loading document...</p>
      </div>
    </div>
  )

  if (loading) {
    if (hideHeader) {
      return loadingContent
    }
    return (
      <WorkPanelWrapper title={fileName}>
        {loadingContent}
      </WorkPanelWrapper>
    )
  }

  // Render error state
  const errorContent = (
    <div className="flex items-center justify-center h-full">
      <div className="flex flex-col items-center gap-4">
        <AlertCircle className="w-8 h-8 text-red-500" />
        <p className="text-sm text-gray-600">{error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 text-sm bg-orange-500 text-white rounded hover:bg-orange-600"
        >
          Retry
        </button>
      </div>
    </div>
  )

  if (error) {
    if (hideHeader) {
      return errorContent
    }
    return (
      <WorkPanelWrapper title={fileName}>
        {errorContent}
      </WorkPanelWrapper>
    )
  }

  // Main content
  const mainContent = (
      <div className={`relative ${hideHeader ? 'w-full' : 'w-full'} bg-gray-100 flex max-h-[80vh]`}>
        
        {/* Page Navigation Controls */}
        <PageNavigation
            currentPage={currentPage}
            numPages={numPages}
            onPageChange={setCurrentPage}
          />
        {/* PDF Viewer Section */}
        <div className="flex-1 relative min-h-0">
          {documentUrl && (
            <React.Suspense fallback={
              <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="flex flex-col items-center gap-6">
                  <Loader2 className="w-16 h-16 animate-spin text-orange-500" />
                  <p className="text-base font-medium text-gray-700">Loading PDF...</p>
                </div>
              </div>
            }>
              <>
                <SimplePDFHighlighter
                  url={documentUrl}
                  annotations={annotations}
                  onAnnotationCreate={handleAnnotationCreate}
                  onAnnotationUpdate={handleAnnotationUpdate}
                  onReady={handlePdfReady}
                  onDocumentLoad={setNumPages}
                  className="w-full"
                  currentPage={currentPage}
                  isDrawing={isDrawing}
                />
                
                {/* Processing Modal Overlay */}
                <ProcessingModal
                  isProcessing={isProcessing}
                  hasAnnotations={annotations.length > 0}
                  progress={progress}
                  progressMessage={progressMessage}
                />
              </>
            </React.Suspense>
          )}
          
          
          {/* Field Summary Panel */}
          <FieldsSummaryPanel fields={fields} />
          
          {/* Draggable Annotation Editor */}
          {selectedAnnotation && (
            <React.Suspense fallback={null}>
              <DraggableAnnotationEditor
                annotation={selectedAnnotation}
                provider={provider}
                onUpdate={handleAnnotationUpdate}
                onClose={() => setSelectedAnnotationId(null)}
              />
            </React.Suspense>
          )}
        </div>
        
        {/* Extraction Panel - Side by side */}
        <FeatureGuard feature="extractionPanel">
          <React.Suspense fallback={
            <div className="w-72 lg:w-80 xl:w-96 bg-white border-l border-gray-200 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          }>
            <ExtractionPanel
              extraction={semanticExtraction}
              onFieldClick={handleFieldClick}
              onEntityClick={handleEntityClick}
              onFieldUpdate={handleFieldUpdate}
              onBulkAction={handleBulkAction}
            />
          </React.Suspense>
        </FeatureGuard>
      </div>
  )

  // Main render - conditionally wrap with WorkPanelWrapper
  if (hideHeader) {
    return mainContent
  }

  return (
    <WorkPanelWrapper 
      title={fileName} 
      actions={
        <DocumentActions
          annotations={annotations}
          currentAnnotationIndex={currentAnnotationIndex}
          isDrawing={isDrawing}
          documentUrl={documentUrl}
          fileName={fileName}
          excelUrl={workItem?.outputs?.excelUrl}
          onToggleDrawing={handleToggleDrawing}
          onNextAnnotation={handleNextAnnotation}
          onPrevAnnotation={handlePrevAnnotation}
        />
      }
    >
      {mainContent}
    </WorkPanelWrapper>
  )
}
